<div class="community-member container-xl py-3" *transloco="let t">
  <div class="mb-3">
    <a class="btn btn-sm btn-link ps-0" [routerLink]="communityLink()">← {{ t('community.thread.back') }}</a>
  </div>

  @if (loading()) {
    <div class="text-center py-4"><div class="spinner-border" role="status"></div></div>
  } @else if (error(); as key) {
    <div class="alert alert-warning">{{ t(key) }}</div>
  } @else if (profile(); as p) {
    @if (actionError(); as key) {
      <div class="alert alert-danger d-flex justify-content-between align-items-start">
        <span>{{ t(key) }}</span>
        <button type="button" class="btn-close" (click)="actionError.set(null)"></button>
      </div>
    }

    <!-- Head: who this is, in one look — avatar, name, where, badges, what you can do. -->
    <section class="card community-member__head mb-3">
      <div class="card-body d-flex flex-wrap align-items-center gap-3">
        <app-community-avatar [user]="p.user" [size]="96" />
        <div class="flex-grow-1 min-w-0">
          <h1 class="h3 mb-1">{{ displayName() }}</h1>
          <div class="text-muted small">
            @if (location(); as where) {
              <span>{{ where }}</span> ·
            }
            {{ t('community.member.member_since', { date: (p.user.member_since | date:'mediumDate') }) }}
            · {{ t('community.member.stats', { threads: p.stats.threads, posts: p.stats.posts }) }}
          </div>
          <div class="d-flex align-items-center flex-wrap gap-1 mt-2">
            <founding-member-badge [foundingMember]="p.user.founding_member" [memberNumber]="p.user.founding_member_number" />
            <founding-member-badge kind="practitioner" [foundingMember]="p.user.founding_practitioner" [memberNumber]="p.user.founding_practitioner_number" />
            <app-community-badges [badges]="p.user.badges" />
            @if (p.user.is_admin) {
              <span class="badge bg-danger">ADMIN</span>
            }
          </div>
        </div>
        <div class="d-flex flex-column gap-2 community-member__actions">
          @if (p.is_self) {
            <a class="btn btn-sm btn-outline-secondary" [routerLink]="profileSettingsLink()">{{ t('community.member.edit_profile') }}</a>
          } @else {
            @if (p.can_message) {
              <button type="button" class="btn btn-sm btn-primary" (click)="message()">✉️ {{ t('community.member.send_message') }}</button>
            } @else {
              <span class="small text-muted" [title]="t('community.member.cannot_message_hint')">{{ t('community.member.cannot_message') }}</span>
            }
            @if (!p.user.is_admin) {
              @if (p.is_blocked) {
                <button type="button" class="btn btn-sm btn-outline-success" [disabled]="busy()" (click)="toggleBlock()">{{ t('community.member.unblock') }}</button>
              } @else {
                <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="busy()" (click)="toggleBlock()">{{ t('community.member.block') }}</button>
              }
            }
          }
        </div>
      </div>
      @if (p.is_blocked) {
        <div class="alert alert-secondary py-2 mx-3 mb-3 small">{{ t('community.member.blocked_notice') }}</div>
      }
    </section>

    <div class="row g-3">
      <!-- Left: the person — about, topics, interests, hobbies, then the professional block. -->
      <div class="col-lg-8 d-flex flex-column gap-3">
        @if (p.bio) {
          <section class="card">
            <div class="card-body">
              <h2 class="h6 fw-semibold mb-2">{{ t('community.member.about') }}</h2>
              <p class="community-member__bio mb-0">{{ p.bio }}</p>
            </div>
          </section>
        }

        @if (hasAbout()) {
          <section class="card">
            <div class="card-body">
              @if (p.about?.exchange_topics?.length) {
                <h2 class="h6 fw-semibold mb-2">{{ t('community.member.talk_about') }}</h2>
                <div class="community-member__chips mb-3">
                  @for (item of p.about!.exchange_topics; track item) { <span class="community-member__chip">{{ item }}</span> }
                </div>
              }
              @if (p.about?.interests?.length) {
                <h3 class="small text-muted fw-semibold mb-2">{{ t('community.member.interests') }}</h3>
                <div class="community-member__chips mb-3">
                  @for (item of p.about!.interests; track item) { <span class="community-member__chip community-member__chip--soft">{{ item }}</span> }
                </div>
              }
              @if (p.about?.hobbies?.length) {
                <h3 class="small text-muted fw-semibold mb-2">{{ t('community.member.hobbies') }}</h3>
                <div class="community-member__chips">
                  @for (item of p.about!.hobbies; track item) { <span class="community-member__chip community-member__chip--soft">{{ item }}</span> }
                </div>
              }
            </div>
          </section>
        }

        @if (p.practitioner) {
          <section class="card community-member__professional">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <h2 class="h6 fw-semibold mb-0">{{ t('community.member.professional') }}</h2>
                <span class="badge bg-info text-dark">{{ t('community.member.professional_public') }}</span>
              </div>
              @if (p.practitioner.calling) {
                <p class="community-member__calling mb-3">{{ p.practitioner.calling }}</p>
              }
              @if (detailEntries().length) {
                <dl class="community-member__details mb-3">
                  @for (entry of detailEntries(); track entry.key) {
                    <dt>{{ t('community.profile.details.' + entry.key) }}</dt>
                    <dd>{{ entry.value }}</dd>
                  }
                </dl>
              }
              <small class="text-muted d-block">
                {{ p.practitioner.accepts_dm ? t('community.member.practitioner_accepts_dm') : t('community.member.practitioner_no_dm') }}
              </small>
              <small class="text-muted d-block mt-1">{{ t('community.member.professional_hint') }}</small>
            </div>
          </section>
        }
      </div>

      <!-- Right: how to reach them, what you share. -->
      <div class="col-lg-4 d-flex flex-column gap-3">
        @if (hasContact()) {
          <section class="card">
            <div class="card-body">
              <h2 class="h6 fw-semibold mb-2">{{ t('community.member.contact') }}</h2>
              <dl class="community-member__contact mb-0">
                @if (p.contact.email) {
                  <dt>{{ t('community.profile.fields.email') }}</dt>
                  <dd><a [href]="'mailto:' + p.contact.email">{{ p.contact.email }}</a></dd>
                }
                @if (p.contact.phone) {
                  <dt>{{ t('community.profile.fields.phone') }}</dt>
                  <dd><a [href]="'tel:' + p.contact.phone">{{ p.contact.phone }}</a></dd>
                }
                @if (p.contact.whatsapp) {
                  <dt>{{ t('community.profile.fields.whatsapp') }}</dt>
                  <dd>
                    @if (whatsappLink(p.contact.whatsapp); as link) {
                      <a [href]="link" target="_blank" rel="noopener noreferrer">{{ p.contact.whatsapp }}</a>
                    } @else {
                      {{ p.contact.whatsapp }}
                    }
                  </dd>
                }
                @if (p.contact.website) {
                  <dt>{{ t('community.profile.fields.website') }}</dt>
                  <dd><a [href]="p.contact.website" target="_blank" rel="noopener noreferrer">{{ p.contact.website }}</a></dd>
                }
                @if (p.contact.city) {
                  <dt>{{ t('community.profile.fields.city') }}</dt>
                  <dd>{{ p.contact.city }}</dd>
                }
                @if (socialLinks().length > 0) {
                  <dt>{{ t('community.profile.fields.social') }}</dt>
                  <dd class="d-flex flex-wrap gap-2">
                    @for (s of socialLinks(); track s.key) {
                      <a class="badge bg-light text-dark text-decoration-none" [href]="s.url" target="_blank" rel="noopener noreferrer"
                         [title]="s.label">{{ t('community.profile.social.' + s.key) }}</a>
                    }
                  </dd>
                }
              </dl>
            </div>
          </section>
        }

        <!-- Shared groups say something between two people; on your own page they are noise. -->
        @if (!p.is_self && p.shared_groups.length > 0) {
          <section class="card">
            <div class="card-body">
              <h2 class="h6 fw-semibold mb-2">{{ t('community.member.shared_groups') }}</h2>
              <div class="d-flex flex-wrap gap-1">
                @for (g of p.shared_groups; track g) {
                  <span class="badge bg-info text-dark">{{ g }}</span>
                }
              </div>
            </div>
          </section>
        }

        @if (p.is_self) {
          <p class="text-muted small mb-0">{{ t('community.member.own_profile_hint') }}</p>
        }
      </div>
    </div>

    @if (!p.practitioner && !hasContact() && !hasAbout() && p.shared_groups.length === 0 && !p.bio) {
      <p class="text-muted small mt-3">{{ t('community.member.nothing_shared') }}</p>
    }
  }
</div>
